import {
    Component,
    Input
} from '@angular/core';

import moment from 'moment-timezone';

import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { RelativeTimezoneDateTypeEnum } from '../../../../api/mentions/enum/relative-timezone-date-type.enum';
import { BriefingDateRangeTypeEnum, RECURRENCE_DATE_RANGE_PLACEHOLDER } from '../../../../Monitor/Briefings/enums/briefing-date-range-type.enum';
import { convertPhpDateFormatToMoment } from '../../../PageBuilder/helpers/timezone.helper';

@Component({
    selector: 'agpr-date-display',
    templateUrl: './date-display.component.html',
    styleUrls: ['./date-display.component.scss'],
    standalone: false,
})
export class DateDisplayComponent {
    // Relative ranges that span a single day are shown as one date, not a start–end range.
    private static readonly SINGLE_DAY_RELATIVE_TYPES: RelativeTimezoneDateTypeEnum[] = [
        RelativeTimezoneDateTypeEnum.TODAY,
        RelativeTimezoneDateTypeEnum.YESTERDAY
    ];

    private _data: any;
    private _referenceDate: Date | number | null = null;

    // Resolved date range (ms timestamps) when the block is configured with a multi-day range.
    dateRange: { start: number; end: number } | null = null;

    // Single resolved date (ms) for single-day relative ranges (TODAY / YESTERDAY).
    singleDate: number | null = null;

    // Timezone the range was queried in, used to format the rendered dates.
    timezone: string | null = null;

    // The literal shown in place of a concrete range when the block derives its range from the
    // recurrence schedule; the real range is resolved by the backend at send time.
    readonly recurrencePlaceholder: string = RECURRENCE_DATE_RANGE_PLACEHOLDER;

    // Whether to render the recurrence placeholder for a RECURRENCE-based range. Enabled by
    // default for the core app's draft/preview; consumers that render the resolved range
    // instead (e.g. the bmie renderer) bind this to false.
    @Input() showRecurrenceDateRangePlaceholder: boolean = true;

    // True when the placeholder should render instead of a date/range.
    get showRecurrencePlaceholder(): boolean {
        return this.showRecurrenceDateRangePlaceholder
            && this._data?.dateRangeType === BriefingDateRangeTypeEnum.RECURRENCE;
    }

    @Input() set data(value: any) {
        this._data = value;
        this.recompute();
    }
    get data(): any {
        return this._data;
    }

    /**
     * Reference point for resolving relative ranges (e.g. LAST_WEEK) — typically the
     * briefing's scheduled send time. Defaults to the current time when not provided.
     */
    @Input() set referenceDate(value: Date | number) {
        this._referenceDate = value ?? null;
        this.recompute();
    }
    get referenceDate(): Date | number | null {
        return this._referenceDate;
    }

    constructor() {}

    get effectiveFormat(): string {
        const styles = this._data?.styles;
        const showTime: boolean = styles?.showTime === true;
        const showTimeZone: boolean = styles?.showTimeZone === true;

        let result = styles?.dateFormat ? convertPhpDateFormatToMoment(styles.dateFormat) : 'MM/DD/YYYY';
        if (showTime) {
            result += ' ' + (styles?.timeFormat || 'h:mm A');
            if (showTimeZone) {
                result += ' z';
            }
        } else if (showTimeZone) {
            result += ' z';
        }
        return result;
    }

    /**
     * Derives what to render from the block's date query: a single date for single-day
     * relative ranges (TODAY / YESTERDAY), a start–end range otherwise, or neither (the
     * block's single `data.date` is used).
     */
    private recompute(): void {
        const dateQuery = this._data?.dateQuery;
        const showTime: boolean = this._data?.styles?.showTime === true;
        const showTimeZone: boolean = this._data?.styles?.showTimeZone === true;
        const stylesTimezone: string = this._data?.styles?.timezone ?? (showTime || showTimeZone ? moment.tz.guess() : null);
        this.dateRange = null;
        this.singleDate = null;

        // A recurrence-based range has no concrete dates to resolve while the placeholder is shown.
        if (this.showRecurrencePlaceholder) {
            this.timezone = null;
            return;
        }

        if (!dateQuery) {
            this.timezone = stylesTimezone;
            return;
        }

        // The backend serves the twig in the search-criteria shape (`dateType` / `timeZone` /
        // `dateRange`), while the front-end DateQueryModel uses (`type` / `timezone` /
        // `absoluteRange`). Normalize so the block renders the range regardless of which
        // shape we are handed.
        const type: DateQueryTypeEnum = dateQuery.type ?? dateQuery.dateType;
        const timezone: string = stylesTimezone ?? dateQuery.timezone ?? dateQuery.timeZone ?? null;
        const absoluteRange = dateQuery.absoluteRange ?? dateQuery.dateRange;
        this.timezone = timezone;

        if (type === DateQueryTypeEnum.ABSOLUTE && absoluteRange?.start && absoluteRange?.end) {
            this.dateRange = {
                start: new Date(absoluteRange.start).getTime(),
                end: new Date(absoluteRange.end).getTime()
            };
            return;
        } else if (type === DateQueryTypeEnum.RELATIVE_TIMEZONE && dateQuery.relativeTimezoneDateType) {
            const relativeType = dateQuery.relativeTimezoneDateType;
            const referenceDate = this._referenceDate ?? undefined;
            const start = DateQueryModel.getStartDateForRelativeTimezone(relativeType, timezone, referenceDate);

            if (DateDisplayComponent.SINGLE_DAY_RELATIVE_TYPES.includes(relativeType)) {
                this.singleDate = start ? +start : null;
                return;
            }

            const end = DateQueryModel.getEndDateForRelativeTimezone(relativeType, timezone, referenceDate);
            if (start && end) {
                this.dateRange = { start: +start, end: +end };
            }

        // HACK: when it's set to "Based on Recurrence Date Range", canvas only gets start & end.. and no type
        } else if (!dateQuery.type && !dateQuery.dateType && dateQuery.dateRange?.start && dateQuery.dateRange?.end) {
            this.dateRange = {
                start: new Date(absoluteRange.start).getTime(),
                end: new Date(absoluteRange.end).getTime()
            };
        }
    }
}
